import {PAdbContext} from "../PA.Core/PAdbContext";
import * as _ from 'lodash';
import * as AsyncLock from 'async-lock';
import {WB_FinishedProductRow, WB_ProductGroupRow} from "../PA.Core/PAdbSchemaRows";
import {esWB_Finished_Product, esWB_Product_Group} from "../PA.Core/PAdbSchema";
import {ILease} from "../PA.Utils/PA.UtilsIF";
import {GenericLease} from "../PA.Utils/GenericLease";
import {LeaseRegistry} from "../PA.Utils/LeaseRegistry";

export class ProductService {

    private static _dbContext: PAdbContext;

    private static dctPrdLock = new AsyncLock();
    private static dctPrdGrpLock = new AsyncLock();
    private static bIsProductFullyLoaded: boolean = false;
    private static bIsPrdGrpFullyLoaded: boolean = false;
    private static dctProductByWbID: { [id: number]: WB_FinishedProductRow } = {};
    private static dctFinishedProductsByCnsWbID: { [id: number]: WB_FinishedProductRow[] } = {};
    private static dctPrdGrpByWbID: { [id: number]: WB_ProductGroupRow } = {};
    private static dctPrdWbIDsByTypeID: { [typeID: string]: { [id: number]: WB_FinishedProductRow } } = {};

    private static _prdLease: ILease;
    private static _prdGrpLease: ILease;

    static getProductByWbID(prdWbID: number): Promise<WB_FinishedProductRow> {
        return new Promise<WB_FinishedProductRow>((resolve, error) => {

            ProductService.dctPrdLock.acquire('global',
                (done: any) => {

                    ProductService.checkProductLease()
                        .then((ignore) => {
                            if (prdWbID in ProductService.dctProductByWbID) {
                                done(null, ProductService.dctProductByWbID[prdWbID]);
                                resolve(ProductService.dctProductByWbID[prdWbID]);
                                return;
                            } else {

                                if (_.isUndefined(ProductService._dbContext)) {
                                    ProductService.instantiateDbContext();
                                }

                                ProductService._dbContext.find(esWB_Finished_Product, prdWbID)
                                    .then((result) => {
                                        if (result instanceof WB_FinishedProductRow) {
                                            ProductService.dctProductByWbID[prdWbID] = result;
                                            done(null, ProductService.dctProductByWbID[prdWbID]);
                                            resolve(result);
                                            return;
                                        }
                                    })
                                    .catch((err) => {
                                        error(err);
                                    });
                            }
                        });
                });
        });
    }

    static getPrdGrpByWbID(prdGrpWbID: number): Promise<WB_ProductGroupRow> {
        return new Promise<WB_ProductGroupRow>((resolve, error) => {

            ProductService.dctPrdGrpLock.acquire('global',
                (done: any) => {

                    ProductService.checkPrdGrpLease()
                        .then((ignore) => {
                            if (prdGrpWbID in ProductService.dctPrdGrpByWbID) {
                                done(null, ProductService.dctPrdGrpByWbID[prdGrpWbID]);
                                resolve(ProductService.dctPrdGrpByWbID[prdGrpWbID]);
                                return;
                            } else {

                                if (_.isUndefined(ProductService._dbContext)) {
                                    ProductService.instantiateDbContext();
                                }

                                ProductService._dbContext.findAll(esWB_Product_Group, {
                                    where: {
                                        WB_PRDGRP_ID: prdGrpWbID,
                                        DELETED_IND: {neq: "1"}
                                    }
                                })
                                    .then((result) => {
                                        if (_.isArray(result)) {
                                            let prdGrpRow: WB_ProductGroupRow = _.first(result);
                                            ProductService.dctPrdGrpByWbID[prdGrpWbID] = prdGrpRow;
                                            done(null, ProductService.dctPrdGrpByWbID[prdGrpWbID]);
                                            resolve(prdGrpRow);
                                            return;
                                        }
                                    })
                                    .catch((err) => {
                                        error(err);
                                    });
                            }
                        });
                });
        });
    }

    static getPrdGrps(prdGrpWbIDs: number[]) : Promise<WB_ProductGroupRow[]> {

        return new Promise<WB_ProductGroupRow[]>((resolve, error) => {
            ProductService.dctPrdGrpLock.acquire('global',
                (done: any) => {
                    let retVal: WB_ProductGroupRow[] = [];
                    let keysToLoad: number[] = [];
                    _.forEach(prdGrpWbIDs, (aId) => {
                        if (aId in ProductService.dctPrdGrpByWbID) {
                            retVal.push(ProductService.dctPrdGrpByWbID[aId]);
                        } else {
                            keysToLoad.push(aId);
                        }
                    });

                    if (_.isEmpty(keysToLoad)) {
                        done(null, ProductService.dctPrdGrpByWbID);
                        resolve(retVal);
                        return;
                    }

                    ProductService.instantiateDbContext();

                    ProductService._dbContext.findAll(esWB_Product_Group, {where: {DELETED_IND: {neq: "1"}, WB_PRDGRP_ID: {inq: keysToLoad}}})
                        .then((results) => {
                            if (_.isArray(results)) {
                                _.forEach(results, (aRecord) => {
                                    ProductService.dctPrdGrpByWbID[aRecord.cif.WB_PRDGRP_ID] = aRecord;
                                    retVal.push(aRecord);
                                })
                            }
                        })
                        .then((ignore) => {
                            done(null, ProductService.dctPrdGrpByWbID);
                            resolve(retVal);
                            return;
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
        })
    }

    static getAllProducts(): Promise<{ [p: number]: WB_FinishedProductRow }> {
        return new Promise<{ [id: number]: WB_FinishedProductRow }>((resolve, error) => {


            ProductService.dctPrdLock.acquire('global',
                (done: any) => {

                    ProductService.checkProductLease()
                        .then(() => {
                            if (ProductService.bIsProductFullyLoaded) {
                                resolve(ProductService.dctProductByWbID);
                                return;
                            }

                            if (_.isUndefined(ProductService._dbContext)) {
                                ProductService.instantiateDbContext();
                            }

                            this.loadAllProducts()
                                .then(() => {
                                    done(null, ProductService.dctProductByWbID);
                                    resolve(ProductService.dctProductByWbID);
                                })
                                .catch((err) => {
                                    error(err);
                                });
                        });
                });
        });
    }

    static async loadAllProducts() : Promise<void> {
        return new Promise<void>((resolve, error) => {

            ProductService.instantiateDbContext();

            if (ProductService.bIsProductFullyLoaded) {
                resolve();
                return;
            }

            ProductService._dbContext.findAll(esWB_Finished_Product, {WHERE: {DELETED_IND: {neq: "1"}}})
                .then((results) => {
                    if (_.isArray(results)) {
                        let prdRows: WB_FinishedProductRow[] = results as WB_FinishedProductRow[];

                        _.forEach(prdRows, (aRow) => {
                            if (!(aRow.cif.WB_PROD_ID in ProductService.dctProductByWbID)) {
                                ProductService.dctProductByWbID[aRow.cif.WB_PROD_ID] = aRow;
                            }
                        });

                        ProductService.bIsProductFullyLoaded = true;
                        resolve();
                    }
                })
                .catch((err) => {
                    error(err);
                });
        })
    }

    static getAllPrdGrps(): Promise<{ [p: number]: WB_ProductGroupRow }> {
        return new Promise<{ [id: number]: WB_ProductGroupRow }>((resolve, error) => {

            ProductService.dctPrdGrpLock.acquire('byLevel',
                (done: any) => {

                    ProductService.checkPrdGrpLease()
                        .then((ignore) => {
                            if (ProductService.bIsPrdGrpFullyLoaded) {
                                done(ProductService.dctPrdGrpByWbID);
                                resolve(ProductService.dctPrdGrpByWbID);
                                return;
                            }

                            if (_.isUndefined(ProductService._dbContext)) {
                                ProductService.instantiateDbContext();
                            }

                            ProductService.loadAllPrdGrps()
                                .then((ignore) => {
                                    done(ProductService.dctPrdGrpByWbID);
                                    resolve(ProductService.dctPrdGrpByWbID);
                                })
                                .catch((err) => {
                                    error(err);
                                });
                        });
                });
        });
    }


    static async getProductsByWbID(prdWbIDs: number[]): Promise<WB_FinishedProductRow[]> {
        if (!prdWbIDs.length) return []

        const retVal: WB_FinishedProductRow[] = [];
        for (const wbId of prdWbIDs) {
            retVal.push(await ProductService.getProductByWbID(wbId));
        }
        return retVal;
    }

    static async getPrdGrpsByWbID(prdGrpWbIDs: number[]): Promise<WB_ProductGroupRow[]> {
        if (!prdGrpWbIDs.length) return [];

        const retVal: WB_ProductGroupRow[] = [];
        for (const wbId of prdGrpWbIDs) {
            retVal.push(await ProductService.getPrdGrpByWbID(wbId));
        }
        return retVal;
    }

    static getAllProdsByType(typeID: string): Promise<{ [p: number]: WB_FinishedProductRow }> {
        return new Promise<{ [p: number]: WB_FinishedProductRow }>((resolve, error) => {
            ProductService.dctPrdLock.acquire('global',
                (done: any) => {

                    ProductService.checkProductLease()
                        .then((ignore) => {

                            ProductService.instantiateDbContext();

                            if (ProductService.bIsProductFullyLoaded) {
                                done(null, ProductService.dctPrdWbIDsByTypeID[typeID]);
                                resolve(ProductService.dctPrdWbIDsByTypeID[typeID]);
                                return;
                            }

                            let query: any = {where: {DELETED_IND: {neq: "1"}, PRODUCT_TYPE: typeID}};
                            return ProductService._dbContext.findAll(esWB_Finished_Product, query);

                        })
                        .then((results) => {
                            ProductService.dctPrdWbIDsByTypeID[typeID] = {};
                            if (_.isArray(results)) {
                                _.forEach(results, (aResult) => {
                                    ProductService.dctPrdWbIDsByTypeID[typeID][aResult.cif.WB_PROD_ID] = aResult;
                                });
                            }

                            done(null, ProductService.dctPrdWbIDsByTypeID[typeID]);
                            resolve(ProductService.dctPrdWbIDsByTypeID[typeID]);
                            return;
                        })
                        .catch((err) => {
                            error(err);
                        });
                });

        });
    }

    static getRelatedFinishedProducts(cnsPrdWbIDs: number[]): Promise<number[]> {

        //get all finished products that are in the cache and have one of the arguments as WB_PROD_ID_CNSPRD_UPC, keep list of ones that aren't
        //take list of ids that aren't in the cache and get all of those records from DB at once, then populate return object and cache as you go
        return new Promise<number[]>((resolve, error) => {

            if (_.isEmpty(cnsPrdWbIDs)) {
                resolve([]);
                return;
            }

            ProductService.checkProductLease()
                .then((ignore) => {
                    ProductService.dctPrdLock.acquire('global',
                        (done: any) => {

                            if (_.isUndefined(ProductService._dbContext)) {
                                ProductService.instantiateDbContext();
                            }

                            let dctReturnValue: { [id: number]: number } = {};
                            let missingKeys: number[] = [];

                            _.forEach(cnsPrdWbIDs, (aWbId) => {
                                if (aWbId in ProductService.dctFinishedProductsByCnsWbID) {
                                    let lstFnsPrds = ProductService.dctFinishedProductsByCnsWbID[aWbId];
                                    _.forEach(lstFnsPrds, (aFndPrd) => {
                                        let aFndPrdId = aFndPrd.cif.WB_PROD_ID;
                                        dctReturnValue[aFndPrdId] = aFndPrdId;
                                    });
                                } else {
                                    missingKeys.push(aWbId);
                                }
                            });

                            if (_.isEmpty(missingKeys)) {
                                done(null, "Success");
                                resolve(_.values(dctReturnValue));
                                return;
                            }

                            ProductService._dbContext.findAll(esWB_Finished_Product, {where: {WB_PROD_ID_CNSPRD_UPC: {inq: missingKeys}}})
                                .then((results) => {
                                    let dctByCnsPrd = _.groupBy(results, (aRow: WB_FinishedProductRow) => aRow.cif.WB_PROD_ID_CNSPRD_UPC);

                                    _.forEach(missingKeys, (aMissingKey) => {
                                        if (aMissingKey in dctByCnsPrd) {
                                            // Add items to return set, fns prd cache, and product cache
                                            let lstFnsPrds = dctByCnsPrd[aMissingKey];
                                            ProductService.dctFinishedProductsByCnsWbID[aMissingKey] = lstFnsPrds;
                                            _.forEach(lstFnsPrds, (aFndPrd) => {
                                                let aFndPrdId = aFndPrd.cif.WB_PROD_ID;
                                                dctReturnValue[aFndPrdId] = aFndPrdId;
                                                ProductService.dctPrdGrpByWbID[aFndPrdId] = aFndPrd;
                                            });
                                        } else {
                                            // Add empty entry so avoid constat searches if the result set is empty
                                            ProductService.dctFinishedProductsByCnsWbID[aMissingKey] = [];
                                        }
                                    });

                                    done(null, "Success");
                                    resolve(_.values(dctReturnValue));
                                })
                                .catch((err) => {
                                    done("Failed");
                                    error(err);
                                });
                        });
                });
        });
    }

    static getRelatedConsumerProducts(fnsPrdWbIDs: number[]): Promise<number[]> {

        return new Promise<number[]>((resolve, error) => {

            if (_.isEmpty(fnsPrdWbIDs)) {
                resolve([]);
                return;
            }

            ProductService.checkProductLease()
                .then((ignore) => {
                    ProductService.dctPrdLock.acquire('global',
                        (done: any) => {

                            if (_.isUndefined(ProductService._dbContext)) {
                                ProductService.instantiateDbContext();
                            }

                            let dctReturnValues: { [id: number]: number } = {};

                            ProductService._dbContext.findUsingCache(esWB_Finished_Product, fnsPrdWbIDs)
                                .then((arrFnsPrds: WB_FinishedProductRow[]) => {
                                    _.forEach(arrFnsPrds, (aRow: WB_FinishedProductRow) => {
                                        if (aRow.cif.WB_PROD_ID_CNSPRD_UPC > 0) {
                                            dctReturnValues[aRow.cif.WB_PROD_ID_CNSPRD_UPC] = aRow.cif.WB_PROD_ID_CNSPRD_UPC;
                                        }
                                        if (!(aRow.cif.WB_PROD_ID in ProductService.dctProductByWbID)) {
                                            ProductService.dctProductByWbID[aRow.cif.WB_PROD_ID] = aRow;
                                        }
                                    });

                                    done(null, "Success");
                                    resolve(_.values(dctReturnValues));
                                })
                                .catch((err) => {
                                    done(err);
                                    error(err);
                                });

                        });
                });
        });
    }

    static async resetProductCache() {
        ProductService.instantiateDbContext();
        ProductService.dctProductByWbID = {};
        ProductService.dctFinishedProductsByCnsWbID = {};
        ProductService.dctPrdWbIDsByTypeID = {};
        ProductService.bIsProductFullyLoaded = false;

        let arr: WB_FinishedProductRow[] = _.values(ProductService.dctProductByWbID);
        await ProductService._dbContext.purgeCache(esWB_Finished_Product, arr);

    }

    static async resetPrdGrpCache() {

        ProductService._dbContext = new PAdbContext();

        let arr: WB_ProductGroupRow[] = _.values(ProductService.dctPrdGrpByWbID);
        await ProductService._dbContext.purgeCache(esWB_Product_Group, arr);

        ProductService.dctPrdGrpByWbID = {};
        ProductService.bIsPrdGrpFullyLoaded = false;
    }

    static async loadAllPrdGrps(): Promise<void> {
        return new Promise<void>((resolve, error) => {
            ProductService.instantiateDbContext();

            if (ProductService.bIsPrdGrpFullyLoaded) {
                resolve();
                return;
            }

            ProductService._dbContext.findAll(esWB_Product_Group, {WHERE: {DELETED_IND: {neq: "1"}}})
                .then((results) => {
                    if (_.isArray(results)) {
                        let prdRows: WB_ProductGroupRow[] = results as WB_ProductGroupRow[];

                        _.forEach(prdRows, (aRow) => {
                            if (!(aRow.cif.WB_PRDGRP_ID in ProductService.dctPrdGrpByWbID)) {
                                ProductService.dctPrdGrpByWbID[aRow.cif.WB_PRDGRP_ID] = aRow;
                            }
                        });

                        ProductService.bIsPrdGrpFullyLoaded = true;
                        resolve();
                    }
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    private static instantiateDbContext() {
        if (_.isUndefined(ProductService._dbContext)) {
            ProductService._dbContext = new PAdbContext();
        }
    }

    private static async checkProductLease(): Promise<void> {
        if (_.isUndefined(ProductService._prdLease)) {
            ProductService.createProductLease();
        }

        if (await ProductService._prdLease.hasDataChanged()) {
            await ProductService.resetProductCache();
            ProductService._prdLease.resetLease();
        }
    }

    private static async checkPrdGrpLease(): Promise<void> {
        if (_.isUndefined(ProductService._prdGrpLease)) {
            ProductService.createPrdGrpLease();
        }

        if (await ProductService._prdGrpLease.hasDataChanged()) {
            await ProductService.resetPrdGrpCache();
            ProductService._prdLease.resetLease();
        }
    }

    private static createProductLease() {
        ProductService._prdLease = new GenericLease(LeaseRegistry.Lease_Type_Product);
    }

    private static createPrdGrpLease() {
        ProductService._prdGrpLease = new GenericLease(LeaseRegistry.Lease_Type_ProductGroup);
    }

}

